/**
 * Shared styles and constants for the Communications module.
 *
 * All tabs, editors, and pickers import from here so colours and spacing
 * stay consistent across the whole module.
 */

import type { SxProps, Theme } from '@mui/material';

// ─── Category model ───────────────────────────────────────────────────────────
//
// Five categories used across Alerts, Round-Up filters, and table badges.
// Safety/Delivery/Loan cover check-based events; MOT-Tax and DVLA are separate.

export type CommCategory = 'safety' | 'delivery' | 'loan' | 'mot' | 'dvla';

export interface CategoryMeta {
  label: string;
  /** Short subtitle shown on category cards */
  subtitle: string;
  color: string;
  bg: string;
  /** Light variant for chip outlines etc. */
  border: string;
}

export const CATEGORY_META: Record<CommCategory, CategoryMeta> = {
  safety: {
    label: 'Safety Checks',
    subtitle: 'Standard safety & location checks',
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#BFDBFE',
  },
  delivery: {
    label: 'Delivery Checks',
    subtitle: 'Outgoing delivery checks',
    color: '#065F46',
    bg: '#ECFDF5',
    border: '#6EE7B7',
  },
  loan: {
    label: 'Loan Checks',
    subtitle: 'Equipment loan checks',
    color: '#6D28D9',
    bg: '#F5F3FF',
    border: '#C4B5FD',
  },
  mot: {
    label: 'MOT / Tax',
    subtitle: 'MOT or road tax expiry',
    color: '#B45309',
    bg: '#FFFBEB',
    border: '#FDE68A',
  },
  dvla: {
    label: 'DVLA',
    subtitle: 'Driver licence events',
    color: '#9D174D',
    bg: '#FDF2F8',
    border: '#F9A8D4',
  },
};

// ─── Tab header ───────────────────────────────────────────────────────────────

/** Identical header used by every Communications list-tab. */
export const commTabHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  mb: 3,
  flexWrap: 'wrap',
  gap: 2,
};

// ─── Table ────────────────────────────────────────────────────────────────────

/** Apply to `<TableRow sx={…}>` for every table header row in the module. */
export const commTableHeadRowSx: SxProps<Theme> = {
  '& th': {
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: 'text.secondary',
    bgcolor: '#FAFAFA',
  },
};

// ─── Empty state ──────────────────────────────────────────────────────────────

export const commEmptyStateSx: SxProps<Theme> = {
  textAlign: 'center',
  py: 8,
  border: '2px dashed',
  borderColor: 'divider',
  borderRadius: 2,
};

// ─── Section label ────────────────────────────────────────────────────────────

export const commSectionLabelSx: SxProps<Theme> = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'text.secondary',
  mb: 1,
};

// ─── Channel block (email / push rows) ────────────────────────────────────────

export const commChannelBlockSx: SxProps<Theme> = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  overflow: 'hidden',
};

export const commChannelRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  p: 1.5,
};
